import * as React from "react";
import { BrowserRouter, Navigate, Route, Routes, useNavigate, useParams } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  AppearanceProvider,
  BrandLoader,
  TooltipProvider,
  useAppearance,
  type Appearance,
} from "@eduviaos/ui-kit";
import { getCurrentTenant } from "@eduviaos/shared";
import { DEFAULT_TENANT_SLUG, institutionFromProfile, type Institution } from "./lib/institution";
import { useTenantProfile } from "./lib/tenantProfile/api";
import { SessionProvider } from "./lib/session";
import { useSession } from "./lib/sessionContext";
import { ZiggyVariantProvider } from "./lib/ziggyProvider";
import { TenantBrandingProvider } from "./components/TenantBrandingProvider";
import { InstitutionMark } from "./components/InstitutionMark";
import { LoginScreen } from "./pages/LoginScreen";
import { MagicLinkVerifyScreen } from "./pages/MagicLinkVerifyScreen";
import { InviteScreen } from "./pages/InviteScreen";
import { HomeScreen } from "./pages/HomeScreen";
import { CertificateVerifyPage } from "./pages/CertificateVerifyPage";

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: false } },
});

const APPEARANCE_STORAGE_KEY = "eos:appearance";

/** Read the persisted appearance the shared AppearanceProvider owns. */
function readStoredAppearance(): Partial<Appearance> {
  try {
    const raw = window.localStorage.getItem(APPEARANCE_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Partial<Appearance>) : {};
  } catch {
    return {};
  }
}

/**
 * Pushes the institution-provisioned palette into the shared appearance
 * engine. Division of ownership: the **institution owns the palette**
 * (from its branding colour, or the platform default when it hasn't picked
 * one), the **learner owns light/dark** (`mode`).
 *
 * We spread the *persisted* appearance under the palette override rather
 * than reading `palette`/`mode` off the live context. This is deliberate:
 * this effect can run before the provider has hydrated its in-memory state
 * (child effects fire before the parent provider's hydration effect), so
 * reading the context would see stale DEFAULTS and calling `set()` would
 * clobber the learner's stored `mode` (and any other axis) back to the
 * default. Re-asserting from storage keeps `mode` — and every other axis —
 * intact no matter the effect ordering, while always applying THIS tenant's
 * palette (the portal is per-tenant, not "first tenant visited wins").
 */
function useInstitutionTheme(institution: Institution | null) {
  const { set } = useAppearance();
  React.useEffect(() => {
    if (!institution) return;
    set({
      ...readStoredAppearance(),
      palette: institution.theme.palette,
      customColor: institution.theme.customColor,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [institution?.slug]);
}

/**
 * Tenant resolution follows the canonical EduViaOS hosting model
 * (Phase P4): the slug is encoded in the **host** — `<slug>--portal-stage`,
 * `<slug>--portal-pr-N`, or (prod) `<slug>.eduviaos.app` — and read from
 * `window.location.hostname`, exactly like IA/apply.
 *
 * `HOST_TENANT` is that host-derived slug, or `null` in local dev / on the
 * bare `portal-stage` host, where the slug lives in the URL path
 * (`/:slug/login`) as a fallback so the whole portal is still reachable
 * without per-tenant subdomains. The two routing shapes below branch on it.
 */
const HOST_TENANT = getCurrentTenant().slug;

/** Login URL for a tenant: bare on the host form, slug-prefixed in dev. */
function loginPath(slug: string): string {
  return HOST_TENANT ? "/login" : `/${slug}/login`;
}

/** App URL for a tenant: bare on the host form, slug-prefixed in dev. */
function appPath(slug: string): string {
  return HOST_TENANT ? "/app" : `/${slug}/app`;
}

/**
 * Boot splash shown while the refresh cookie is replayed on cold load. A
 * calm, branded hold — the institution's mark with the EduViaOS loader
 * beneath it — so the learner never sees a login-form flash before an
 * existing session resolves. Pinned to the viewport so it stays centred
 * whatever the parent's height; the loader handles reduced motion.
 */
function BootSplash({ institution }: { institution: Institution | null }) {
  const label = institution ? `Loading ${institution.shortName}…` : "Loading…";
  return (
    <div className="portal-aurora bg-background text-foreground fixed inset-0 z-50 grid place-items-center px-6">
      <div className="flex flex-col items-center gap-5">
        <InstitutionMark
          vertical={institution?.vertical ?? "study_centre"}
          name={institution?.name ?? "EduViaOS"}
          size="md"
        />
        <BrandLoader size={36} label={label} />
        <p aria-hidden="true" className="text-muted-foreground text-sm font-medium">
          {label}
        </p>
      </div>
    </div>
  );
}

/**
 * Shown when the slug in the host/path matches no tenant. Previously this
 * silently rendered a school called "Bright Future Public School", which
 * meant a typo'd subdomain produced a convincing portal for an institution
 * that doesn't exist. Saying so is the only honest option.
 */
function UnknownTenantScreen({ slug }: { slug: string | undefined }) {
  return (
    <div className="portal-aurora bg-background text-foreground grid min-h-dvh place-items-center px-6">
      <div className="max-w-sm space-y-3 text-center">
        {/*
         * Vertical-neutral wording on purpose: with no tenant resolved there
         * is no vertical to speak the language of, and "centre" would read
         * wrong to a school parent who mistyped a subdomain.
         */}
        <h1 className="font-display text-2xl font-semibold">
          We couldn&apos;t find that institution
        </h1>
        <p className="text-muted-foreground text-sm">
          {slug ? (
            <>
              Nothing is registered at <span className="font-medium">{slug}</span>. Check the link
              your institution sent you — the address usually looks like{" "}
              <span className="font-medium">your-institution.eduviaos.com</span>.
            </>
          ) : (
            <>This portal address is missing its institution. Check the link you were sent.</>
          )}
        </p>
      </div>
    </div>
  );
}

/**
 * Resolves the tenant a route is serving. Every route needs the same three
 * outcomes (still loading / no such tenant / here it is), so they share one
 * hook and the two route components stay about session state.
 */
function useResolvedInstitution(slug: string | undefined): {
  institution: Institution | null;
  isLoading: boolean;
  notFound: boolean;
} {
  const { data, isLoading, isError } = useTenantProfile(slug);
  const institution = React.useMemo(() => (data ? institutionFromProfile(data) : null), [data]);
  return { institution, isLoading: isLoading && !isError, notFound: isError };
}

function CertificateVerifyRoute({ slug: slugOverride }: { slug?: string }) {
  const params = useParams();
  const slug = slugOverride ?? params.slug;
  const { institution, isLoading, notFound } = useResolvedInstitution(slug);
  useInstitutionTheme(institution);
  if (notFound) {
    return <UnknownTenantScreen slug={slug} />;
  }
  if (isLoading || !institution) {
    return <BootSplash institution={institution} />;
  }
  return <CertificateVerifyPage institution={institution} />;
}

function LoginRoute({ slug: slugOverride }: { slug?: string }) {
  const params = useParams();
  const slug = slugOverride ?? params.slug;
  const { institution, isLoading, notFound } = useResolvedInstitution(slug);
  const { status } = useSession();
  useInstitutionTheme(institution);
  if (notFound) {
    return <UnknownTenantScreen slug={slug} />;
  }
  // While the refresh cookie is being replayed — or the tenant is still
  // resolving — we don't yet know if a live session exists; hold the splash
  // rather than flashing the login form.
  if (isLoading || !institution || status === "loading") {
    return <BootSplash institution={institution} />;
  }
  if (status === "authenticated") {
    return <Navigate to={appPath(institution.slug)} replace />;
  }
  return <LoginScreen institution={institution} />;
}

/**
 * Learner invite acceptance (Phase SC-4c). Anonymous by design — the whole
 * point is that the learner has no password yet — so it renders regardless
 * of session status. An already-signed-in learner who re-opens the link
 * from their email is sent to the app instead of being asked to set a
 * password again.
 */
function InviteRoute({ slug: slugOverride }: { slug?: string }) {
  const params = useParams();
  const slug = slugOverride ?? params.slug;
  const token = params.token ?? "";
  const navigate = useNavigate();
  const { institution, isLoading, notFound } = useResolvedInstitution(slug);
  const { status } = useSession();
  useInstitutionTheme(institution);
  if (notFound) {
    return <UnknownTenantScreen slug={slug} />;
  }
  if (isLoading || !institution || status === "loading") {
    return <BootSplash institution={institution} />;
  }
  if (status === "authenticated") {
    return <Navigate to={appPath(institution.slug)} replace />;
  }
  return (
    <InviteScreen
      institution={institution}
      token={token}
      loginHref={loginPath(institution.slug)}
      onSignedIn={() => navigate(appPath(institution.slug), { replace: true })}
    />
  );
}

function MagicLinkVerifyRoute({ slug: slugOverride }: { slug?: string }) {
  const params = useParams();
  const slug = slugOverride ?? params.slug;
  const token = new URLSearchParams(window.location.search).get("token") ?? "";
  const navigate = useNavigate();
  const { institution, isLoading, notFound } = useResolvedInstitution(slug);
  const { status } = useSession();
  useInstitutionTheme(institution);
  if (notFound) {
    return <UnknownTenantScreen slug={slug} />;
  }
  if (isLoading || !institution || status === "loading") {
    return <BootSplash institution={institution} />;
  }
  if (status === "authenticated") {
    return <Navigate to={appPath(institution.slug)} replace />;
  }
  return (
    <MagicLinkVerifyScreen
      institution={institution}
      token={token}
      loginHref={loginPath(institution.slug)}
      onSignedIn={() => navigate(appPath(institution.slug), { replace: true })}
    />
  );
}

function AppRoute({ slug: slugOverride }: { slug?: string }) {
  const params = useParams();
  const slug = slugOverride ?? params.slug;
  const { institution, isLoading, notFound } = useResolvedInstitution(slug);
  const { status } = useSession();
  useInstitutionTheme(institution);
  if (notFound) {
    return <UnknownTenantScreen slug={slug} />;
  }
  if (isLoading || !institution || status === "loading") {
    return <BootSplash institution={institution} />;
  }
  if (status === "anonymous") {
    return <Navigate to={loginPath(institution.slug)} replace />;
  }
  return <HomeScreen institution={institution} />;
}

/** Host-form routes: slug comes from the subdomain, not the path. */
function HostRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginRoute slug={HOST_TENANT ?? undefined} />} />
      <Route
        path="/login/verify"
        element={<MagicLinkVerifyRoute slug={HOST_TENANT ?? undefined} />}
      />
      <Route path="/invite/:token" element={<InviteRoute slug={HOST_TENANT ?? undefined} />} />
      <Route
        path="/verify/:code"
        element={<CertificateVerifyRoute slug={HOST_TENANT ?? undefined} />}
      />
      <Route path="/app/*" element={<AppRoute slug={HOST_TENANT ?? undefined} />} />
      {/* Back-compat: pre-P4 path-based URLs drop the redundant slug. */}
      <Route path="/:slug/login" element={<Navigate to="/login" replace />} />
      <Route path="/:slug/app/*" element={<Navigate to="/app" replace />} />
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

/** Dev / bare-host routes: slug carried in the URL path. */
function PathRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Navigate to={`/${DEFAULT_TENANT_SLUG}/login`} replace />} />
      <Route path="/:slug/login" element={<LoginRoute />} />
      <Route path="/:slug/login/verify" element={<MagicLinkVerifyRoute />} />
      <Route path="/:slug/invite/:token" element={<InviteRoute />} />
      <Route path="/:slug/verify/:code" element={<CertificateVerifyRoute />} />
      <Route path="/:slug/app/*" element={<AppRoute />} />
      <Route path="*" element={<Navigate to={`/${DEFAULT_TENANT_SLUG}/login`} replace />} />
    </Routes>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      {/*
       * Phase F2c — apply the tenant's REAL brand (logo + primary/secondary
       * hex the IA sets via /me/branding). The slug is host-derived, so
       * branding lands on stage/prod per-tenant hosts
       * (`<slug>--portal-stage`); on the bare dev host it's null and the
       * provider no-ops. Inline CSS vars on <html> beat the `data-palette`
       * stylesheet rules, so this wins regardless of nesting order with
       * AppearanceProvider.
       */}
      <TenantBrandingProvider slug={HOST_TENANT}>
        <AppearanceProvider>
          <TooltipProvider delayDuration={200}>
            <SessionProvider>
              <ZiggyVariantProvider>
                <BrowserRouter>{HOST_TENANT ? <HostRoutes /> : <PathRoutes />}</BrowserRouter>
              </ZiggyVariantProvider>
            </SessionProvider>
          </TooltipProvider>
        </AppearanceProvider>
      </TenantBrandingProvider>
    </QueryClientProvider>
  );
}
